import { defineQueries, defineQuery } from '@rocicorp/zero'
import { z } from 'zod'
import type { AuthData } from './auth.ts'
import { builder } from './builder.ts'

// Every sensitive named query filters from the trusted server context. Clients
// can invoke only these registered query shapes, so authorization lives here.
const userIdArg = z.object({ userId: z.string() })
const dateRangeArg = z.object({ start: z.number(), end: z.number() })

// Histories page by growing this limit, so a client can never request an
// unbounded sync of a member's lifetime ledger.
const pagedUserArg = z.object({
  userId: z.string(),
  limit: z.number().int().min(1).max(500),
})
const guestArg = z.object({ userId: z.string(), guestId: z.string() })

/**
 * The live feed is a "what just happened" ticker, not a history: it starts
 * empty on each load and fills as orders arrive. Pinning the cutoff to when the
 * page opened also keeps yesterday's traffic off the wire entirely.
 */
const sinceArg = z.object({ since: z.number() })

/** Account data (money, ledgers, guests) belongs to its member and to admins. */
function isOwnerOrAdmin(ctx: AuthData | undefined, userId: string): boolean {
  return (
    !!ctx && (ctx.role === 'admin' || (ctx.role === 'member' && ctx.sub === userId))
  )
}

export const queries = defineQueries({
  /** Approved, non-deleted members — shared with authenticated club roles. */
  roster: defineQuery(() =>
    builder.users
      .where('isApproved', true)
      .where('deletedAt', 'IS', null)
      .orderBy('displayName', 'asc'),
  ),

  /** Active public names plus admin-only legal names for local import matching. */
  importMemberCandidates: defineQuery(({ ctx }) =>
    ctx?.role === 'admin'
      ? builder.users
          .where('deletedAt', 'IS', null)
          .related('private')
          .orderBy('displayName', 'asc')
      : builder.users.where(({ or }) => or()).related('private'),
  ),

  /** One private profile, readable only by its owner or an admin. */
  userPrivateById: defineQuery(userIdArg, ({ args, ctx }) =>
    isOwnerOrAdmin(ctx, args.userId)
      ? builder.usersPrivate.where('userId', args.userId).one()
      : builder.usersPrivate.where(({ or }) => or()).one(),
  ),

  // ── Admin member management (US-18). ────────────────────────────────────

  /** Active and deleted members, for the admin roster. */
  allMembers: defineQuery(({ ctx }) =>
    ctx?.role === 'admin'
      ? builder.users.orderBy('displayName', 'asc')
      : builder.users.where(({ or }) => or()),
  ),

  /** Full name, email and date of birth for every member. Admin only. */
  memberPrivateRoster: defineQuery(({ ctx }) =>
    ctx?.role === 'admin'
      ? builder.usersPrivate.orderBy('userId', 'asc')
      : builder.usersPrivate.where(({ or }) => or()),
  ),

  /**
   * PINs actually assigned to a member — the roster column and the local
   * generator's taken-set. The `userId IS NOT NULL` filter is what keeps the
   * reserved kiosk row server-side: `pins` is in the schema, but that row is
   * never in any result.
   */
  assignedPins: defineQuery(({ ctx }) =>
    ctx?.role === 'admin'
      ? builder.pins.where('userId', 'IS NOT', null).orderBy('pinCode', 'asc')
      : builder.pins.where(({ or }) => or()),
  ),

  /** A member's own PIN (US-08 parity). Never the reserved row. */
  myPin: defineQuery(({ ctx }) =>
    ctx?.role === 'member' || ctx?.role === 'admin'
      ? builder.pins.where('userId', ctx.sub).one()
      : builder.pins.where(({ or }) => or()).one(),
  ),

  /**
   * One member's welfare and kit detail. Owner or admin, and deliberately
   * per-member: the edit form subscribes while open so this tier never syncs
   * roster-wide.
   */
  memberProfile: defineQuery(userIdArg, ({ args, ctx }) =>
    isOwnerOrAdmin(ctx, args.userId)
      ? builder.usersProfile.where('userId', args.userId).one()
      : builder.usersProfile.where(({ or }) => or()).one(),
  ),

  /** The drinks catalogue. Public. */
  drinksCatalogue: defineQuery(() =>
    builder.drinks.where('deletedAt', 'IS', null).orderBy('description', 'asc'),
  ),

  /** A single member (for page headers). Public roster data. */
  userById: defineQuery(userIdArg, ({ args }) =>
    builder.users
      .where('id', args.userId)
      .where('isApproved', true)
      .where('deletedAt', 'IS', null)
      .one(),
  ),

  /** A member's active guests (profile management + ordering a guest a drink). */
  guestsForUser: defineQuery(userIdArg, ({ args, ctx }) =>
    ctx?.role === 'kiosk' || isOwnerOrAdmin(ctx, args.userId)
      ? builder.guests
          .where('userId', args.userId)
          .where('deletedAt', 'IS', null)
          .orderBy('name', 'asc')
      : builder.guests.where(({ or }) => or()),
  ),

  /** All active guests available to kiosk/admin ordering screens. */
  orderingGuests: defineQuery(({ ctx }) =>
    ctx?.role === 'kiosk' || ctx?.role === 'admin'
      ? builder.guests.where('deletedAt', 'IS', null).orderBy('name', 'asc')
      : builder.guests.where(({ or }) => or()),
  ),

  // ── Balance inputs. Unbounded, but each is filtered to OPEN rows only, so a
  //    settled member syncs nothing and an unsettled one syncs only what they
  //    owe. Paid history is the part that grows, and that is what pages below.

  /** Unpaid drinks — the balance input and the outstanding list. */
  outstandingDrinksForUser: defineQuery(userIdArg, ({ args, ctx }) =>
    isOwnerOrAdmin(ctx, args.userId)
      ? builder.userDrinks
          .where('userId', args.userId)
          .where('paidAt', 'IS', null)
          .related('drink')
          .related('guest')
          .orderBy('purchasedAt', 'desc')
      : builder.userDrinks
          .where(({ or }) => or())
          .related('drink')
          .related('guest'),
  ),

  /** Outstanding, non-deleted fees — a balance input. */
  outstandingFeesForUser: defineQuery(userIdArg, ({ args, ctx }) =>
    isOwnerOrAdmin(ctx, args.userId)
      ? builder.fees
          .where('userId', args.userId)
          .where('deletedAt', 'IS', null)
          .where('paidAt', 'IS', null)
          .orderBy('feeDate', 'desc')
      : builder.fees.where(({ or }) => or()),
  ),

  /** Credits with something left to spend — a balance input. */
  unusedCreditsForUser: defineQuery(userIdArg, ({ args, ctx }) =>
    isOwnerOrAdmin(ctx, args.userId)
      ? builder.credits
          .where('userId', args.userId)
          .where('usedAt', 'IS', null)
          .orderBy('createdAt', 'desc')
      : builder.credits.where(({ or }) => or()),
  ),

  // ── Paginated histories (US-12). Newest-first, owner/admin only.

  /** Every drink on a member's tab, paid or not. */
  drinkHistoryForUser: defineQuery(pagedUserArg, ({ args, ctx }) =>
    isOwnerOrAdmin(ctx, args.userId)
      ? builder.userDrinks
          .where('userId', args.userId)
          .related('drink')
          .related('guest')
          .orderBy('purchasedAt', 'desc')
          .orderBy('id', 'desc')
          .limit(args.limit)
      : builder.userDrinks
          .where(({ or }) => or())
          .related('drink')
          .related('guest'),
  ),

  /** Every non-deleted fee, paid or not. */
  feeHistoryForUser: defineQuery(pagedUserArg, ({ args, ctx }) =>
    isOwnerOrAdmin(ctx, args.userId)
      ? builder.fees
          .where('userId', args.userId)
          .where('deletedAt', 'IS', null)
          .orderBy('feeDate', 'desc')
          .orderBy('createdAt', 'desc')
          .limit(args.limit)
      : builder.fees.where(({ or }) => or()),
  ),

  /** Every credit, used or not. */
  creditHistoryForUser: defineQuery(pagedUserArg, ({ args, ctx }) =>
    isOwnerOrAdmin(ctx, args.userId)
      ? builder.credits
          .where('userId', args.userId)
          .orderBy('createdAt', 'desc')
          .orderBy('id', 'desc')
          .limit(args.limit)
      : builder.credits.where(({ or }) => or()),
  ),

  /** Every payment, including cancelled and pending ones. */
  paymentHistoryForUser: defineQuery(pagedUserArg, ({ args, ctx }) =>
    isOwnerOrAdmin(ctx, args.userId)
      ? builder.payments
          .where('userId', args.userId)
          .related('importTransaction')
          .orderBy('createdAt', 'desc')
          .orderBy('paymentId', 'desc')
          .limit(args.limit)
      : builder.payments.where(({ or }) => or()).related('importTransaction'),
  ),

  /**
   * One guest's drinks (US-13). Unbounded because the subtotal must cover every
   * row and a single guest's drink count is bounded by one person's visits.
   * Ownership is pinned in the query, so a guessed guestId returns nothing.
   */
  guestTab: defineQuery(guestArg, ({ args, ctx }) =>
    isOwnerOrAdmin(ctx, args.userId)
      ? builder.userDrinks
          .where('userId', args.userId)
          .where('guestId', args.guestId)
          .related('drink')
          .orderBy('purchasedAt', 'desc')
      : builder.userDrinks.where(({ or }) => or()).related('drink'),
  ),

  /** Existing fees for local duplicate detection on one selected fee date. */
  feesForImportDate: defineQuery(dateRangeArg, ({ args, ctx }) =>
    ctx?.role === 'admin'
      ? builder.fees
          .where('deletedAt', 'IS', null)
          .where('feeDate', '>=', args.start)
          .where('feeDate', '<', args.end)
          .orderBy('feeDate', 'desc')
      : builder.fees.where(({ or }) => or()),
  ),

  /** Bank payments in the statement range, used for local duplicate warnings. */
  bankTransferPaymentsForRange: defineQuery(dateRangeArg, ({ args, ctx }) =>
    ctx?.role === 'admin'
      ? builder.payments
          .where('paymentMethod', 'bank_transfer')
          .where('cancelledAt', 'IS', null)
          .where('createdAt', '>=', args.start)
          .where('createdAt', '<', args.end)
          .orderBy('createdAt', 'desc')
      : builder.payments.where(({ or }) => or()),
  ),

  /** Successful imported rows in the statement range, used for audit duplicate warnings. */
  bankImportTransactionsForRange: defineQuery(dateRangeArg, ({ args, ctx }) =>
    ctx?.role === 'admin'
      ? builder.importTransactions
          .where('transactionDate', '>=', args.start)
          .where('transactionDate', '<', args.end)
          .orderBy('transactionDate', 'desc')
      : builder.importTransactions.where(({ or }) => or()),
  ),

  /**
   * Sanitized live activity: no payment linkage or financial-status columns.
   */
  recentOrders: defineQuery(sinceArg, ({ args, ctx }) =>
    ctx?.role === 'kiosk' || ctx?.role === 'admin'
      ? builder.orderActivity
          .where('purchasedAt', '>=', args.since)
          .orderBy('purchasedAt', 'desc')
          .limit(25)
      : ctx?.role === 'member'
        ? builder.orderActivity
            .where('userId', ctx.sub)
            .where('purchasedAt', '>=', args.since)
            .orderBy('purchasedAt', 'desc')
            .limit(25)
        : builder.orderActivity.where(({ or }) => or()),
  ),
})
